import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useContext,
} from 'react'
import { observer } from 'mobx-react-lite'
import { LuCheckCircle } from 'react-icons/lu'
import './OneStop.scss'
import { Form } from 'views/forms/Form'
import useLocalStorageState from 'hooks/use-localstorage-state/use-localstorage-state'
import { OneStopPlanScreen } from 'views/onestop'
import { GlobalStoreContext } from 'stores/GlobalStore'
import { useTranslation } from 'react-i18next'
import { useDynamicFormContent } from 'hooks'

interface MenuItem {
  id: number
  label: string
  queryParam: string
  color: 'blue' | 'grey' | 'green'
  number: number
}

const OneStopTopMenu: React.FC<{ phase: number }> = observer(({ phase }) => {
  const globalStore = useContext(GlobalStoreContext)
  const globalState = globalStore?.state
  const formEventState = globalState?.formEvents
  const { t } = useTranslation(undefined, {
    keyPrefix: 'oneStop',
  })
  const { formContent, mailinBallotDeadlinePassed } = useDynamicFormContent()
  const hideMib = formContent?.oneStop?.hideMib
  const menuItems: MenuItem[] = [
    {
      id: 1,
      label: t('headers.registration'),
      queryParam: 'register',
      color: 'grey',
      number: 1,
    },
    {
      id: 2,
      label: t('headers.requestBallot'),
      queryParam: 'request-ballot',
      color: 'grey',
      number: 2,
    },
    {
      id: 3,
      label: t('headers.vote'),
      queryParam: 'plan-to-vote',
      color: 'grey',
      number: hideMib || mailinBallotDeadlinePassed ? 2 : 3,
    },
  ]

  if (hideMib || mailinBallotDeadlinePassed) {
    menuItems.splice(1, 1)
  }

  const handleClick = (id: number) => {
    try {
      setTimeout(() => {
        try {
          globalStore?.setState({
            ...globalStore?.state,
            oneStop: {
              ...globalStore?.state?.oneStop,
              phase: id,
            },
          })
          if (
            globalStore?.state?.initialPath &&
            globalStore.state.initialPath !== window.location.pathname
          ) {
            window.history.pushState(
              {},
              '',
              globalStore.state.initialPath + window.location.search,
            )
          }
        } catch (e) {
          console.error(e)
        }
      }, 50)
    } catch (e) {}
  }

  window.completeThirdOneStopPhase = () => {
    globalStore?.setState({
      ...globalStore?.state,
      formEvents: {
        ...globalStore?.state?.formEvents,
        thirdOneStopPhaseComplete: true,
      },
    })
  }

  const getItemClass = useCallback(
    (item: MenuItem) => {
      if (formEventState) {
        const {
          registeredStatusLookupSuccess,
          voterRegistrationSubmitSuccess,
          mailInBallotSubmitSuccess,
          thirdOneStopPhaseComplete,
        } = formEventState
        if (
          item.id === 1 &&
          (registeredStatusLookupSuccess || voterRegistrationSubmitSuccess)
        ) {
          return 'one-stop__item--complete'
        }

        if (item.id === 2 && mailInBallotSubmitSuccess) {
          return 'one-stop__item--complete'
        }
        if (item.id === 3 && thirdOneStopPhaseComplete) {
          return 'one-stop__item--complete'
        }
      }

      if (item.id === phase) {
        return 'one-stop__item--current'
      }
      return 'one-stop__item--future'
    },
    [phase, formEventState],
  )

  return (
    <ul className="one-stop__menu">
      {menuItems.map((item, index) => (
        <>
          {index > 0 && <span className="one-stop__spacer">—</span>}
          <li
            key={item.id}
            className={`one-stop__item ${getItemClass(item)}`}
            onClick={() => handleClick(item.id)}
          >
            <span
              className={`one-stop__number${
                item.id === phase ? ' one-stop__number--current' : ''
              }`}
            >
              {item.number}
            </span>
            <span className="one-stop__check">
              <LuCheckCircle />
            </span>
            <span
              className={`one-stop__label${
                item.id === phase ? ' one-stop__label--current' : ''
              }`}
            >
              {item.label}
            </span>
          </li>
        </>
      ))}
    </ul>
  )
})

export const OneStop: React.FC = observer(() => {
  const globalState = useContext(GlobalStoreContext)?.state
  const phase =
    globalState?.oneStop?.phase !== undefined ? globalState?.oneStop?.phase : 1
  const updateRegistration =
    globalState?.oneStop?.updateRegistration !== undefined
      ? globalState?.oneStop?.updateRegistration
      : false
  const [state, setState] = useLocalStorageState('one-stop__data', {
    phase: 1,
  } as {
    [key: string]: any
  })
  const [hideForm, setHideForm] = useState(false)
  const [showForm, setShowForm] = useState(true)
  const showLocations = process.env.REACT_APP_SHOW_LOCATIONS ? true : false
  useEffect(() => {
    const loadLocations = async () => {
      if (!showLocations) {
        return
      }
      const baseUrl = process.env.REACT_APP_LOCATIONS_BASE_PATH
      const manifestResponse = await fetch(`${baseUrl}/asset-manifest.json`)

      if (!manifestResponse.ok) {
        throw new Error(
          `Failed to fetch asset manifest: ${manifestResponse.statusText}`,
        )
      }

      const {
        files: { 'main.css': mainCss = '', 'main.js': mainJs = '' } = {},
      } = await manifestResponse.json()

      if (!mainCss || !mainJs) {
        throw new Error('Main CSS or JS file not found in asset manifest')
      }

      document.head.appendChild(
        Object.assign(document.createElement('link'), {
          rel: 'stylesheet',
          href: mainCss.replace('find-voting-location/', ''),
        }),
      )
      document.body.appendChild(
        Object.assign(document.createElement('script'), {
          src: mainJs.replace('find-voting-location/', ''),
          async: true,
        }),
      )
    }
    loadLocations()
  }, [])

  const formApiId = useMemo(() => {
    switch (phase) {
      case 1:
        return updateRegistration ? 'voter-registration' : 'status-lookup'
      case 2:
        return 'mail-in-ballot'
      default:
        return null
    }
  }, [phase, updateRegistration])

  useEffect(() => {
    setHideForm(true)
    setTimeout(() => {
      setHideForm(false)
    }, 50)
  }, [formApiId])

  const OneStopLocations: React.FC = () => {
    const locationsWrapperId = 'voting-location-search-container'
    useEffect(() => {
      setTimeout(() => {
        if (phase === 3 && window.renderLocationSearch) {
          window.renderLocationSearch(locationsWrapperId, true)
        }
      }, 500)
    }, [])
    return <div id={locationsWrapperId}></div>
  }

  const content = useMemo(() => {
    return (
      <>
        {showForm ? (
          formApiId ? (
            hideForm ? null : (
              <Form apiName={formApiId} />
            )
          ) : showLocations ? (
            <OneStopLocations />
          ) : (
            <OneStopPlanScreen />
          )
        ) : null}
      </>
    )
  }, [showForm, hideForm, showLocations, formApiId])

  return (
    <nav className={`one-stop one-stop--phase--${phase}`}>
      <OneStopTopMenu phase={phase} />
      {content}
    </nav>
  )
})
